import type { ShoppingCart } from '@optionway/models';

interface SelectedOfferStore {
  // Screen memory rather than domain data: cleared ($reset) by every new pricing and prebook.
  priceIncreaseShown: boolean;
}

// The only door the funnel reads the offer through: the raw responses stay in search-pricing and search-prebook.
export const useSelectedOfferStore = defineStore('selected-offer', {
  state: (): SelectedOfferStore => ({
    priceIncreaseShown: false,
  }),
  getters: {
    // The prebook answers without bag allowances: only what it reprices is taken from it.
    offer(): ShoppingCart | undefined {
      const pricing = useSearchPricingStore().result;
      const prebook = useSearchPrebookStore().result;
      if (!pricing || !prebook) {
        return pricing;
      }
      return {
        ...pricing,
        flight: { ...pricing.flight, price: prebook.flight.price },
        bookableBags: prebook.bookableBags,
        bestPriceBookableBags: prebook.bestPriceBookableBags,
      };
    },
    priceIncreaseSinceSearch(): number {
      return increase(
        useSelectedSearchResultStore().result?.price?.total,
        useSearchPricingStore().result?.flight.price?.total,
      );
    },
    prebookPriceIncrease(): number {
      const pricing = useSearchPricingStore().result;
      const prebook = useSearchPrebookStore().result;
      const luggages = useLuggagesStore();
      if (!pricing || !prebook) {
        return 0;
      }
      return increase(
        (pricing.flight.price?.total ?? 0) + luggages.selectedBagsTotalPriceFor(pricing.bookableBags ?? []),
        (prebook.flight.price?.total ?? 0) + luggages.selectedBagsTotalPrice,
      );
    },
    latestPriceIncrease(): number {
      return useSearchPrebookStore().result ? this.prebookPriceIncrease : this.priceIncreaseSinceSearch;
    },
    unseenPriceIncrease(): number {
      return this.priceIncreaseShown ? 0 : this.latestPriceIncrease;
    },
    hasUnseenPriceIncrease(): boolean {
      return this.unseenPriceIncrease > 0;
    },
  },
  actions: {
    markPriceIncreaseShown() {
      this.priceIncreaseShown = true;
    },
  },
});

// used by priceIncreaseSinceSearch, prebookPriceIncrease
function increase(before?: number, after?: number): number {
  if (before === undefined || after === undefined) {
    return 0;
  }
  return Math.max(0, roundToCents(after - before));
}
